import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import type { LocationDescriptor } from 'history';

/**
 * Create a type safe navigation hook for a specific set of routes
 *
 * This is an initial pass at creating some more robust routing infrastructure.
 * Currently we are manually constructing paths all over the app using string
 * template literals which is error prone, especially during refactors. There
 * is much more room for iteration here in the future.
 *
 * What does it do?
 * - Creates a navigational hook for a specific set of predefined routes.
 * - Exposes a location descriptor, navigation function, and replace function.
 * - Provides type safety for url params and deals with url generation.
 *
 * @example
 * ```ts
 * // --- domain navigation file
 * import { createPath, createNavigationHook } from 'src/shared/navigation/RouteNavigationToolkit';
 *
 * const useDomainNavigation = createNavigationHook({
 *   someRoute: createPath('/route/here'),
 *   someRouteWithParams: createPath<{ testparam: string }>('/route/here/:testparam'),
 * });
 *
 * // --- implementation file
 * import { useDomainNavigation } from 'path/to/domain/hooks/navigation';
 *
 * const Test = () => {
 *   const { navigate } = useDomainNavigation();
 *
 *   return (
 *     <button onClick={() => navigate.someRouteWithParams({ testparam: 'beep' })}>
 *       {'button text'}
 *     </button>
 *   );
 * };
 * ```
 */

const pathSymbol = Symbol('RoutePath');

type PathParams = Record<string, string | number> | undefined;

type RoutePath<_ extends PathParams = PathParams> = {
    __type: typeof pathSymbol;
    path: string;
};

type ParamsFromRoutePath<D> = D extends RoutePath<infer P> ? P : undefined;

export function createPath<P extends PathParams = undefined>(path: string): RoutePath<P> {
    return {
        __type: pathSymbol,
        path,
    };
}

function replacePathParams<P extends RoutePath>(path: string, params: ParamsFromRoutePath<P>) {
    if (!params) {
        return path;
    }

    return Object.entries(params).reduce(
        (accum, [key, value]) => accum.replace(`:${key}`, `${value}`),
        path,
    );
}

type LocationDescriptorFunctions<R extends Record<string, RoutePath>> = Readonly<{
    [K in keyof R]: undefined extends ParamsFromRoutePath<R[K]>
        ? () => LocationDescriptor
        : (params: ParamsFromRoutePath<R[K]>) => LocationDescriptor;
}>;

type NavigationFunctions<R extends Record<string, RoutePath>> = Readonly<{
    [K in keyof R]: undefined extends ParamsFromRoutePath<R[K]>
        ? () => void
        : (params: ParamsFromRoutePath<R[K]>) => void;
}>;

export function createNavigationHook<R extends Record<string, RoutePath>>(routes: R) {
    const locationDescriptor = Object.entries(routes).reduce((accum, [k, v]) => {
        return Object.assign(accum, {
            [k]: params => replacePathParams(v.path, params),
        });
    }, {} as LocationDescriptorFunctions<R>);

    return function useDomainNavigation() {
        const history = useHistory();

        const navigate = useMemo(
            () =>
                Object.keys(routes).reduce((accum, k) => {
                    return Object.assign(accum, {
                        [k]: params => history.push(locationDescriptor[k](params)),
                    });
                }, {} as NavigationFunctions<R>),
            [history],
        );

        const replace = useMemo(
            () =>
                Object.keys(routes).reduce((accum, k) => {
                    return Object.assign(accum, {
                        [k]: params => history.replace(locationDescriptor[k](params)),
                    });
                }, {} as NavigationFunctions<R>),
            [history],
        );

        return {
            locationDescriptor,
            navigate,
            replace,
            goBack: history.goBack,
        };
    };
}
